import { Component, OnInit, ViewChild } from '@angular/core';
import { DriverStationStopService } from "./driver-station-stop.service";
import { Driver, DriverStationStop } from "../../model/data-managemnt";
import { DriverService } from "./driver.service";
import { DataManagementService } from "../data-management/data-management.service";
import { ToastController } from '@ionic/angular';
import { AnimationController } from '@ionic/angular';
import { Router } from "@angular/router";

@Component({
  selector: 'app-driver',
  templateUrl: './driver.component.html',
  styleUrls: ['./driver.component.scss'],
})
export class DriverComponent implements OnInit {
  selectedPersonNumber: number | null = null;
  customNumber: number | null = null;
  isCustomInput: boolean = false;
  presetNumbers: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
  driver: Driver;
  locationCache: Map<number, string> = new Map();
  driverStationStops: DriverStationStop[] = [];
  loading: boolean = false;
  saveSuccessful: boolean = false;
  recentSelections: number[] = [];
  currentView: string = 'selection'; // Default view is number selection

  constructor(
    private driverStationStopService: DriverStationStopService,
    private dataManagementService: DataManagementService,
    private driverService: DriverService,
    public router: Router,
    private toastController: ToastController,
    private animationCtrl: AnimationController
  ) {}

  ngOnInit() {
    this.loading = true;
    this.loadDriverData();
    this.loadRecentSelections();
  }

  loadDriverData() {
    this.driverService.getOneByIdUser(this.dataManagementService.currentUser.id).subscribe(
      driver => {
        this.driver = driver;
        // console.log("Driver data loaded:", this.driver);
        this.loading = false;
      },
      error => {
        if (error.status === 401) {
          this.handleAuthError();
        }
        // console.error("Error loading driver data:", error);
        this.loading = false;
        this.presentToast('Failed to load driver information', 'danger');
      }
    );
  }

  loadStationStops() {
    this.loading = true;
    this.driverStationStopService.getDriverStationStopByIdDriver(this.driver.idDriver).subscribe(
      response => {
        this.driverStationStops = response.data || [];
        this.driverStationStops.forEach(driverStationStop => {
          this.getLocation(driverStationStop.lat, driverStationStop.lng, driverStationStop.id);
        })
        // console.log("Driver station stops loaded:", this.driverStationStops);
        this.loading = false;
      },
      error => {
        if (error.status === 401) {
          this.handleAuthError();
        }
        // console.error("Error loading station stops:", error);
        this.loading = false;
        this.presentToast('Failed to load station stops', 'danger');
      }
    );
  }

  refreshStationStops(event: any) {
    this.driverStationStopService.getDriverStationStopByIdDriver(this.driver.idDriver).subscribe(
      response => {
        this.driverStationStops = response.data || [];
        // console.log("Driver station stops refreshed:", this.driverStationStops);
        event.target.complete();
      },
      error => {
        // console.error("Error refreshing station stops:", error);
        event.target.complete();
        this.presentToast('Failed to refresh stops', 'danger');
      }
    );
  }

  handleAuthError() {
    this.dataManagementService.currentUser = null;
    localStorage.removeItem('currentUser');
    localStorage.removeItem('token');
    localStorage.removeItem("groups");
    localStorage.removeItem("pointInterests");
    this.router.navigate(['/signin']);
  }

  // Load recent selections from local storage
  loadRecentSelections() {
    const saved = localStorage.getItem('recentPersonNumbers');
    if (saved) {
      try {
        this.recentSelections = JSON.parse(saved);
      } catch (e) {
        // console.error('Error parsing saved selections', e);
        this.recentSelections = [];
      }
    }
  }

  // Save recent selection to local storage
  saveRecentSelection(number: number) {
    // Remove if already exists
    this.recentSelections = this.recentSelections.filter(n => n !== number);

    // Add to beginning
    this.recentSelections.unshift(number);

    // Keep only last 5
    if (this.recentSelections.length > 5) {
      this.recentSelections = this.recentSelections.slice(0, 5);
    }

    // Save to local storage
    localStorage.setItem('recentPersonNumbers', JSON.stringify(this.recentSelections));
  }

  selectNumber(number: number): void {
    this.selectedPersonNumber = number;
    this.isCustomInput = false;

    // Animate the selected number
    this.animateSelection();
  }

  toggleCustomInput(): void {
    this.isCustomInput = !this.isCustomInput;
    if (!this.isCustomInput) {
      this.customNumber = null;
    } else {
      this.selectedPersonNumber = null;
    }
  }

  validateCustomNumber(event: any): void {
    if (event.target.value) {
      this.selectedPersonNumber = Number(event.target.value);
      // Animate the selection display
      setTimeout(() => this.animateSelection(), 100);
    }
  }

  animateSelection() {
    const selectionEl = document.querySelector('.selection-display');
    if (selectionEl) {
      const animation = this.animationCtrl.create()
        .addElement(selectionEl)
        .duration(300)
        .iterations(1)
        .keyframes([
          { offset: 0, transform: 'scale(0.95)', opacity: '0.8' },
          { offset: 0.5, transform: 'scale(1.05)', opacity: '1' },
          { offset: 1, transform: 'scale(1)', opacity: '1' }
        ]);

      animation.play();
    }
  }

  saveDriverStationStop(): void {
    if (this.selectedPersonNumber) {
      this.loading = true;
      const driverStationStop = new DriverStationStop();
      driverStationStop.personNumber = this.selectedPersonNumber;
      driverStationStop.driver = this.driver;

      // Get current position if available
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            driverStationStop.lat = position.coords.latitude;
            driverStationStop.lng = position.coords.longitude;
            this.finalizeSaveStationStop(driverStationStop);
          },
          (error) => {
            // console.warn("Geolocation error:", error);
            // Continue saving without coordinates
            this.finalizeSaveStationStop(driverStationStop);
          }
        );
      } else {
        // Geolocation not supported
        this.finalizeSaveStationStop(driverStationStop);
      }
    }
  }

  finalizeSaveStationStop(driverStationStop: DriverStationStop): void {
    driverStationStop.time = new Date();

    this.driverStationStopService.addDriverStationStop(driverStationStop)
      .subscribe({
        next: (response) => {
          // console.log('Driver station stop saved successfully', response);
          this.loading = false;
          this.saveSuccessful = true;
          this.presentToast('Driver station stop saved successfully', 'success');

          // If we're in list view, refresh the list
          if (this.currentView === 'list') {
            this.loadStationStops();
          }

          // Save to recent selections
          this.saveRecentSelection(this.selectedPersonNumber);

          // Reset selection after brief delay to show success state
          setTimeout(() => {
            this.selectedPersonNumber = null;
            this.customNumber = null;
            this.saveSuccessful = false;
          }, 2000);
        },
        error: (error) => {
          // console.error('Error saving driver station stop', error);
          this.loading = false;
          this.presentToast('Failed to save driver station stop', 'danger');
        }
      });
  }

  async presentToast(message: string, color: string) {
    const toast = await this.toastController.create({
      message: message,
      duration: 2000,
      color: color,
      position: 'bottom',
      cssClass: 'custom-toast'
    });
    toast.present();
  }

  getShortUserName(): string {
    if (!this.driver) return '';
    return `${this.driver.firstName} ${this.driver.lastName}`;
  }

  segmentChanged(event: any) {
    // If switching to list view, load the station stops
    if (event.detail.value === 'list' && this.driver && !this.loading) {
      this.loadStationStops();
    }
  }
  getLocation(lat: number, lng: number, scanId: number): void {
    this.dataManagementService.inverseGeoconding(lat, lng).subscribe(
      (response) => {
        // console.log(response);
        this.locationCache.set(scanId, response?.display_name || 'Location not found');

      },
      (error) => {
        // console.error('Error fetching location:', error);
        this.locationCache.set(scanId, 'Unable to fetch location');
      }
    );
  }
}
